import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import adminService from '../services/admin';
import toast from 'react-hot-toast';
import useUISettings from '../hooks/useUISettings';
import {
  AdminLayout,
  UserManagement,
  PlanManagement,
  SiteContentManagement,
  APIProviderManagement,
  PaymentHistory,
  AdminSettings,
} from '../components/Admin';

// Admin Overview Component
function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { adminQuickActions } = useUISettings();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await adminService.getStats();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card text-center">
              <p className="text-gray-400 text-sm mb-1">Total Users</p>
              <p className="text-3xl font-bold">{stats.users.total}</p>
            </div>
            <div className="card text-center">
              <p className="text-gray-400 text-sm mb-1">Active Users</p>
              <p className="text-3xl font-bold text-green-400">{stats.users.active}</p>
            </div>
            <div className="card text-center">
              <p className="text-gray-400 text-sm mb-1">Paid Users</p>
              <p className="text-3xl font-bold text-purple-400">{stats.users.paid ?? stats.users.premium}</p>
            </div>
            <div className="card text-center">
              <p className="text-gray-400 text-sm mb-1">Today's Conversions</p>
              <p className="text-3xl font-bold">{stats.tts.today_conversions}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-lg font-semibold mb-4">TTS Statistics</h3>
              <div className="space-y-2 text-gray-400">
                <p>Total Conversions: <span className="text-white">{stats.tts.total_conversions}</span></p>
                <p>Today's Conversions: <span className="text-white">{stats.tts.today_conversions}</span></p>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold mb-4">Voice Clones</h3>
              <div className="space-y-2 text-gray-400">
                <p>Total Clones: <span className="text-white">{stats.voice_clones.total}</span></p>
                <p>Active Clones: <span className="text-white">{stats.voice_clones.active}</span></p>
              </div>
            </div>
          </div>

          <div className="card mt-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              {adminQuickActions.map((action) => (
                <Link key={`${action.path}-${action.label}`} to={action.path} className="btn btn-secondary">
                  {action.label}
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Admin() {
  return (
    <AdminLayout>
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="plans" element={<PlanManagement />} />
        <Route path="content" element={<SiteContentManagement />} />
        <Route path="api-providers" element={<APIProviderManagement />} />
        <Route path="payments" element={<PaymentHistory />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminLayout>
  );
}

export default Admin;
